import produce from "immer";
import { keyBy, omit } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useHistory } from "react-router";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Install, InstallCommission } from "../../utils/types";
import usePrevious from "../../utils/usePrevious";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";
import { useToast } from "../toast";
import { useWorkspaces } from "../workspaces";
import InstallFormSidebar from "./InstallFormSidebar";

interface InstallsMap {
  [installId: string]: Install;
}

type ContextValue = {
  installs: InstallsMap;
  addInstall: (
    values: InstallValues
  ) => Promise<ApiResponse<Install> | undefined>;
  updateInstall: (
    installId: string,
    values: InstallValues
  ) => Promise<ApiResponse<Install>>;
  removeInstall: (installId: string) => void;
  fetchInstalls: () => Promise<InstallsMap>;
  toggleInstallSidebar: (contextualInstall?: Install | undefined) => void;
  currentlyEditedInstall: Install | undefined;
  setCurrentlyEditedInstall: (install: Install | undefined) => void;
  updateCommission: (
    installId: string,
    commission: InstallCommission
  ) => Promise<ApiResponse<InstallCommission>>;
  removeCommission: (
    installId: string,
    commissionId: string
  ) => Promise<ApiResponse<[]>>;
};

const InstallsContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const InstallsProvider: React.FC<Props> = ({
  children,
  ...otherProps
}) => {
  const { currentWorkspaceId, currentWorkspaceIsLocked, setWorkspaces } =
    useWorkspaces();
  const { addLoader, removeLoader } = useLoading();
  const [installs, setInstalls] = useState<InstallsMap>({});
  const [openInstallFormSidebar, setOpenInstallFormSidebar] = useState(false);
  const [currentlyEditedInstall, setCurrentlyEditedInstall] = useState<
    Install | undefined
  >();
  const history = useHistory();
  const { addToast } = useToast();

  const fetchInstalls = useCallback(async () => {
    if (!currentWorkspaceId) return {};

    addLoader(LoadingKey.workspaceInstalls);

    const res = await fetchApi(`/api/workspace/${currentWorkspaceId}/installs`);

    const apiResponse: ApiResponse<Install[]> = await res.json();

    let newInstalls = {};

    if (apiResponse.status) {
      newInstalls = keyBy(apiResponse.data, "uuid");

      setInstalls(newInstalls);
    }

    removeLoader(LoadingKey.workspaceInstalls);

    return newInstalls;
  }, [addLoader, currentWorkspaceId, removeLoader]);

  const prevWorkspaceId = usePrevious(currentWorkspaceId);
  useEffect(() => {
    if (
      currentWorkspaceId &&
      prevWorkspaceId &&
      currentWorkspaceId !== prevWorkspaceId
    ) {
      setInstalls({});
    }
  }, [currentWorkspaceId, prevWorkspaceId]);

  useEffect(() => {
    if (currentWorkspaceIsLocked) return;

    fetchInstalls();
  }, [currentWorkspaceIsLocked, fetchInstalls]);

  const addInstall = useCallback(
    async (values: InstallValues) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/installs`,
        {
          method: "POST",
          body: JSON.stringify(values)
        }
      );

      const apiResponse: ApiResponse<Install> = await response.json();

      if (apiResponse.status) {
        await fetchInstalls();

        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.install += 1;
          })
        );
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchInstalls, setWorkspaces]
  );

  const updateInstall = useCallback(
    async (installId: string, values: InstallValues) => {
      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/installs/${installId}`,
        {
          method: "PUT",
          body: JSON.stringify(values)
        }
      );

      const apiResponse: ApiResponse<Install> = await response.json();

      if (apiResponse.status) {
        await fetchInstalls();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchInstalls]
  );

  const removeInstall = useCallback(
    async (installId: string) => {
      if (!currentWorkspaceId) return;

      setInstalls((prevLocations) => omit(prevLocations, [installId]));

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/installs/${installId}`,
        { method: "DELETE" }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      if (apiResponse.status) {
        fetchInstalls();

        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.install -= 1;
          })
        );
      } else {
        addToast({
          type: "error",
          message: apiResponse.message || "Failed to remove install"
        });
      }

      return apiResponse;
    },
    [addToast, currentWorkspaceId, fetchInstalls, setWorkspaces]
  );

  const toggleInstallSidebar = useCallback(
    (contextualInstall?: Install | undefined) => {
      setCurrentlyEditedInstall(contextualInstall);

      if (openInstallFormSidebar) {
        setOpenInstallFormSidebar(false);
        return;
      }

      setOpenInstallFormSidebar(true);
    },
    [openInstallFormSidebar]
  );

  const onSuccessInstallForm = useCallback(
    (install?: Install | undefined) => {
      if (history.location.pathname.includes("/workspace")) {
        return;
      }

      const InstallUuid = install?.uuid
        ? install.uuid
        : currentlyEditedInstall?.uuid;

      history.push(`/installs/${InstallUuid}/info`);
    },
    [currentlyEditedInstall?.uuid, history]
  );

  const updateCommission = useCallback(
    async (installId: string, commission: InstallCommission) => {
      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/install/${installId}/commissions/${commission.uuid}`,
          {
            method: "PUT",
            body: JSON.stringify(commission)
          }
        );

        const apiResponse: ApiResponse<InstallCommission> =
          await response.json();

        if (!apiResponse.status) return apiResponse;

        fetchInstalls();

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [currentWorkspaceId, fetchInstalls]
  );

  const removeCommission = useCallback(
    async (installId: string, commissionId: string) => {
      try {
        const response = await fetchApi(
          `/api/workspace/${currentWorkspaceId}/install/${installId}/commissions/${commissionId}`,
          {
            method: "DELETE"
          }
        );

        const apiResponse: ApiResponse<[]> = await response.json();

        if (!apiResponse.status) return apiResponse;

        fetchInstalls();

        return apiResponse;
      } catch {
        throw Error("Something went wrong");
      }
    },
    [currentWorkspaceId, fetchInstalls]
  );

  const value = useMemo(
    () => ({
      installs,
      fetchInstalls,
      addInstall,
      updateInstall,
      removeInstall,
      toggleInstallSidebar,
      currentlyEditedInstall,
      setCurrentlyEditedInstall,
      updateCommission,
      removeCommission
    }),
    [
      installs,
      fetchInstalls,
      addInstall,
      updateInstall,
      removeInstall,
      toggleInstallSidebar,
      currentlyEditedInstall,
      setCurrentlyEditedInstall,
      updateCommission,
      removeCommission
    ]
  );

  return (
    <InstallsContext.Provider value={value} {...otherProps}>
      <InstallFormSidebar
        open={openInstallFormSidebar}
        toggleEditInstallForm={toggleInstallSidebar}
        install={currentlyEditedInstall}
        onSuccess={onSuccessInstallForm}
      />
      {children}
    </InstallsContext.Provider>
  );
};

export const useInstalls = (): ContextValue => {
  const context = useContext(InstallsContext);
  if (context === undefined) {
    throw new Error("useInstalls must be used within an InstallsProvider");
  }
  return context;
};

//
// Utils
//

export interface InstallValues {
  hotspot_uuid: string;
  location_uuid: string;
  installed_at: string | null; // e.g. "2021-08-04"
  uninstalled_at: string | null; // e.g. "2021-08-04"
  commissions: InstallCommission[];
}
